import React, { useState } from 'react';
import { Layout, Menu, Button, Space, Dropdown } from 'antd';
import { 
  DashboardOutlined, 
  TeamOutlined, 
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  CalendarOutlined,
  ProjectOutlined,
  FileTextOutlined,
  FieldTimeOutlined,
  ScheduleOutlined,
  FlagOutlined,
  SunOutlined,
  UserOutlined,
  KeyOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import ChangePasswordModal from './ChangePasswordModal';

const { Sider, Content, Header } = Layout;

interface MainLayoutProps {
  children: React.ReactNode;
  /** Pages built from the shared e22 cards sit directly on the grey background instead of one white box. */
  bare?: boolean;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children, bare }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [collapsed, setCollapsed] = useState(isMobile);
  const [changePasswordVisible, setChangePasswordVisible] = useState(false);
  const { t } = useTranslation();

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleChangePassword = () => {
    setChangePasswordVisible(true);
  };

  const menuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: <Link to="/dashboard">{t('navigation.dashboard')}</Link>,
    },
    {
      key: '/feeds',
      icon: <FileTextOutlined />,
      label: <Link to="/feeds">{t('navigation.feeds')}</Link>,
    },
    {
      key: '/employees',
      icon: <TeamOutlined />,
      label: <Link to="/employees">{t('navigation.employees')}</Link>,
    },
    {
      key: '/attendance',
      icon: <FieldTimeOutlined />,
      label: <Link to="/attendance">{t('navigation.attendance')}</Link>,
    },
    {
      key: '/projects',
      icon: <ProjectOutlined />,
      label: <Link to="/projects">{t('navigation.projects')}</Link>,
    },
    {
      key: '/tasks',
      icon: <ScheduleOutlined />,
      label: <Link to="/tasks">{t('navigation.tasks')}</Link>,
    },
    {
      key: '/calendar-events',
      icon: <CalendarOutlined />,
      label: <Link to="/calendar-events">{t('navigation.calendarEvents')}</Link>,
    },
    {
      key: '/public-holidays',
      icon: <FlagOutlined />,
      label: <Link to="/public-holidays">{t('navigation.publicHolidays')}</Link>,
    },
    {
      key: '/vacation',
      icon: <SunOutlined />,
      label: <Link to="/vacation">{t('navigation.vacations')}</Link>,
    },
  ];

  // Detail pages keep their parent menu item highlighted
  const selectedKey = location.pathname.startsWith('/attendance-employee')
    ? '/attendance'
    : location.pathname;

  const userMenuItems = [
    {
      key: 'changePassword',
      icon: <KeyOutlined />,
      label: t('auth.changePassword'),
      onClick: handleChangePassword,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('navigation.logout'),
      onClick: handleLogout,
    },
  ];

  return (
    <Layout style={{ height: '100vh', overflow: 'hidden' }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        breakpoint="md"
        onBreakpoint={(broken) => {
          setIsMobile(broken);
          setCollapsed(broken);
        }}
        collapsedWidth={isMobile ? 0 : 80}
        className="e22-sider"
        style={{
          background: '#fff',
          boxShadow: '2px 0 6px rgba(0, 21, 41, 0.12)',
          // The menu stays put; it gets its own scrollbar only if it is taller than the window
          height: '100vh',
          overflow: 'auto',
        }}
      >
        <div style={{
          margin: '16px',
          height: '54px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden'
        }}>
          <img
            src="/estethica-logo.png"
            alt="Estethica Logo"
            style={{
              maxHeight: collapsed ? '40px' : '54px',
              width: 'auto'
            }}
          />
        </div>
        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}
          items={menuItems}
          onClick={({ key, domEvent }) => {
            // The label's <Link> already navigates; only handle clicks elsewhere on the row (icon, padding)
            if ((domEvent.target as HTMLElement).closest('a')) return;
            navigate(key);
          }}
          style={{ borderRight: 0 }}
          inlineCollapsed={collapsed}
        />
      </Sider>
      <Layout style={{ height: '100vh', overflow: 'hidden' }}>
        <Header style={{ 
          background: '#fff', 
          padding: '0 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 1px 6px rgba(15, 23, 42, 0.08)',
          flex: 'none',
          zIndex: 20,
        }}>
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{ fontSize: '16px', width: 64, height: 64 }}
          />
          <Space>
            <LanguageSwitcher />
            <Dropdown
              menu={{ items: userMenuItems }}
              placement="bottomRight"
              trigger={['click']}
            >
              <Button 
                type="text" 
                icon={<UserOutlined />}
                style={{ display: 'flex', alignItems: 'center' }}
              >
                {t('navigation.profile')}
              </Button>
            </Dropdown>
          </Space>
        </Header>
        {bare ? (
          <Content id="e22-main" className="e22-content-bare e22-content-scroll">
            {children}
          </Content>
        ) : (
          <Content
            id="e22-main"
            className="e22-content-scroll"
            style={{ margin: '24px 16px', padding: 24, background: '#fff', boxShadow: '0 2px 8px rgba(0, 21, 41, 0.30)', borderRadius: '8px' }}
          >
            {children}
          </Content>
        )}
      </Layout>
      
      <ChangePasswordModal
        visible={changePasswordVisible}
        onCancel={() => setChangePasswordVisible(false)}
      />
    </Layout>
  );
};

export default MainLayout; 